import { useState, useEffect, useCallback } from 'react';
import { Atom, ArrowsClockwise, CaretDown, CaretRight } from '@phosphor-icons/react';

import { getSpineManager, type PhysicsConstraintInfo } from '../../lib/spine-manager';
import { getPixiApp } from '../../lib/pixi-app';
import { useSceneStore } from '../../stores/sceneStore';
import { Slider } from '../ui/slider';
import type { Layer, PhysicsConstraintPose } from '@spine-workspace/shared';

interface PhysicsConstraintEditorProps {
  layer: Layer;
}

export default function PhysicsConstraintEditor({ layer }: PhysicsConstraintEditorProps) {
  const [constraints, setConstraints] = useState<PhysicsConstraintInfo[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const { setLayerPhysicsPose } = useSceneStore();

  const refresh = useCallback(() => {
    const c = getSpineManager().getPhysicsConstraints(layer.id);
    if (c && c.length > 0) setConstraints(c);
  }, [layer.id]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const app = getPixiApp();
    if (!app) return;
    app.ticker.add(refresh);
    return () => { app.ticker?.remove(refresh); };
  }, [refresh]);

  if (constraints.length === 0) {
    return (
      <p className="text-text-tertiary text-[11px] font-sans italic">
        No physics constraints in this asset
      </p>
    );
  }

  function savePose(name: string, overrides?: Partial<PhysicsConstraintPose>) {
    const c = constraints.find((c) => c.name === name);
    if (!c) return;
    setLayerPhysicsPose(layer.id, name, {
      mix: c.mix,
      wind: c.wind,
      gravity: c.gravity,
      inertia: c.inertia,
      strength: c.strength,
      damping: c.damping,
      massInverse: c.massInverse,
      ...overrides,
    });
  }

  function handleParam(
    name: string,
    param: keyof Omit<PhysicsConstraintInfo, 'name' | 'bone' | 'active'>,
    value: number
  ) {
    getSpineManager().setPhysicsParam(layer.id, name, param, value);
    savePose(name, { [param]: value });
  }

  function handleReset(name: string) {
    getSpineManager().resetPhysicsConstraint(layer.id, name);
    // Re-read fresh values after reset
    const fresh = getSpineManager().getPhysicsConstraints(layer.id);
    const c = fresh?.find((c) => c.name === name);
    if (c) {
      setLayerPhysicsPose(layer.id, name, {
        mix: c.mix,
        wind: c.wind,
        gravity: c.gravity,
        inertia: c.inertia,
        strength: c.strength,
        damping: c.damping,
        massInverse: c.massInverse,
      });
    }
    refresh();
  }

  function toggleExpanded(name: string) {
    setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));
  }

  return (
    <div className="space-y-5">
      {constraints.map((c) => (
        <div key={c.name} className="space-y-2">
          {/* Header row */}
          <div className="flex items-center gap-2">
            <Atom size={12} className="text-violet shrink-0" />
            <span className="font-mono text-xs text-accent font-medium truncate flex-1">{c.name}</span>
            <span className="text-[10px] text-text-tertiary font-sans bg-bg-elevated px-1.5 py-0.5 rounded shrink-0">
              {c.bone}
            </span>
            <button
              title="Reset to setup pose"
              onClick={() => handleReset(c.name)}
              className="w-5 h-5 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast shrink-0"
            >
              <ArrowsClockwise size={11} />
            </button>
          </div>

          {/* Mix slider — primary control */}
          <SliderRow
            label="Mix"
            value={c.mix}
            min={0} max={1} step={0.01}
            display={`${Math.round(c.mix * 100)}%`}
            onChange={(v) => handleParam(c.name, 'mix', v)}
          />

          {/* Wind slider */}
          <SliderRow
            label="Wind"
            value={c.wind}
            min={-50} max={50} step={0.5}
            display={c.wind.toFixed(1)}
            onChange={(v) => handleParam(c.name, 'wind', v)}
          />

          {/* Gravity slider */}
          <SliderRow
            label="Gravity"
            value={c.gravity}
            min={-50} max={50} step={0.5}
            display={c.gravity.toFixed(1)}
            onChange={(v) => handleParam(c.name, 'gravity', v)}
          />

          {/* Advanced toggle */}
          <button
            onClick={() => toggleExpanded(c.name)}
            className="flex items-center gap-1 text-[10px] text-text-tertiary hover:text-text-secondary font-sans transition-colors duration-fast"
          >
            {expanded[c.name]
              ? <CaretDown size={9} />
              : <CaretRight size={9} />
            }
            Advanced
          </button>

          {/* Advanced params */}
          {expanded[c.name] && (
            <div className="space-y-2 pl-2 border-l border-border-subtle">
              <SliderRow
                label="Inertia"
                value={c.inertia}
                min={0} max={1} step={0.01}
                display={c.inertia.toFixed(2)}
                onChange={(v) => handleParam(c.name, 'inertia', v)}
              />
              <SliderRow
                label="Strength"
                value={c.strength}
                min={0} max={1} step={0.01}
                display={c.strength.toFixed(2)}
                onChange={(v) => handleParam(c.name, 'strength', v)}
              />
              <SliderRow
                label="Damping"
                value={c.damping}
                min={0} max={1} step={0.01}
                display={c.damping.toFixed(2)}
                onChange={(v) => handleParam(c.name, 'damping', v)}
              />
              <SliderRow
                label="Mass⁻¹"
                value={c.massInverse}
                min={0} max={10} step={0.1}
                display={c.massInverse.toFixed(1)}
                onChange={(v) => handleParam(c.name, 'massInverse', v)}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

interface SliderRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (v: number) => void;
}

function SliderRow({ label, value, min, max, step, display, onChange }: SliderRowProps) {
  return (
    <div className="flex items-center gap-3">
      <span className="font-sans text-xs text-text-secondary w-[52px] shrink-0">{label}</span>
      <Slider
        min={min} max={max} step={step}
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        className="flex-1"
      />
      <span className="font-mono text-xs text-text-tertiary w-[36px] text-right shrink-0">
        {display}
      </span>
    </div>
  );
}
